<template>
    <div
        :id="id"
        class="gauss-chart">
        <div
            :id="`${id}-d3-wrapper`"
            class="gauss-chart__d3-wrapper"></div>
        <template v-if="chartReady">
            <div class="gauss-chart__current-value">
                <p>{{ capitalize(activePoint.desc) }}</p>
            </div>
            <div class="gauss-chart__fake-axis">
                <ul class="gauss-chart__fake-axis-list">
                    <li class="fake-tick">
                        <p>{{ tickLabels[0] }}</p>
                    </li>
                    <li class="fake-tick">
                        <p>{{ tickLabels[1] }}</p>
                    </li>
                    <li class="fake-tick">
                        <p>{{ tickLabels[2] }}</p>
                    </li>
                </ul>
            </div>
        </template>
    </div>
</template>

<script>
// @ is an alias to /src

import Vue from "vue";
import axios from "axios";

import d3 from "@/utils/d3/d3.js";
import { v4 as uuidv4 } from "uuid";

export default {
    name: "GaussChart",
    props: {
        activePoint: {
            type: Object,
            required: true
        },
        gaussianCurve: {
            type: Array,
            required: true
        },
        tickLabels: {
            type: Array,
            required: false,
            default: function() {
                return [this.$t("mediocre"), this.$t("average"), this.$t("exceptional")];
            }
        }
    },
    data() {
        return {
            chartReady: false,
            id: "gauss-chart-" + uuidv4()
        };
    },
    computed: {
        screenWidth() {
            return this.$store.getters["responsiveModule/getCurrentWindowWidth"];
        },
        chartColumnWidth() {
            let width = 192;

            if (this.isXSmall) {
                width = (this.screenWidth - 40) / 3;
            }

            if (this.isSmall) {
                width = 170;
            }

            return width;
        },
        cleanedData() {
            if(this.gaussianCurve && this.activePoint) {
                let cleanedData = this.gaussianCurve.map((item) => {
                    return {
                        "x": item.x,
                        "y": item.y,
                        "highlight": false,
                    };
                });

                cleanedData.push({
                    "x": this.activePoint.x,
                    "y": this.activePoint.y,
                    "highlight": true
                });

                const orderedCleanedData = cleanedData.sort((a, b) => {
                    return parseFloat(a.x) - parseFloat(b.x);
                });

                return orderedCleanedData;
            }
        },
    },
    watch: {
        screenWidth(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.resetPotentialChart();
                this.buildPotentialChart();
            }
        },
        cleanedData(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.resetPotentialChart();
                this.buildPotentialChart();
            }
        }
    },
    mounted: function() {
        // Responsive Behavior
        /* istanbul ignore next */
        this.$responsive.registerMediaQuery("x-small", () => {
            this.isXSmall = true;
        }, () => {
            this.isXSmall = false;
        });

        // Responsive Behavior
        /* istanbul ignore next */
        this.$responsive.registerMediaQuery("small", () => {
            this.isSmall = true;
        }, () => {
            this.isSmall = false;
        });

        // Responsive Behavior
        /* istanbul ignore next */
        this.$responsive.registerMediaQuery("from-medium", () => {
            this.isMobile = false;
        }, () => {
            this.isMobile = true;
        });

        this.buildPotentialChart();
    },
    methods: {
        resetPotentialChart() {
            const wrapper = document.getElementById(`${this.id}-d3-wrapper`);

            wrapper.innerHTML = "";
        },
        buildPotentialChart() {
            Vue.nextTick(() => {
                const yValueRange = d3.extent(this.cleanedData, function(d) {
                    return d.y;
                });
                const adaptedYValueRange = [yValueRange[0], yValueRange[1]];

                // set the dimensions and margins of the graph
                var margin = {top: 50, right: 30, bottom: 100, left: 40};
                let width = this.screenWidth;

                if (width > 918) {
                    width = 918;
                }

                var height = 435 + margin.top + margin.bottom;

                // append the svg object to the body of the page
                var svg = d3.select(`#${this.id}-d3-wrapper`)
                    .append("svg")
                    .attr("preserveAspectRatio", "xMinYMin meet")
                    .attr("width", "100%")
                    .attr("height", height)
                    .attr("viewBox", `0 0 ${width} ${height + margin.top + 100}`)
                    .append("svg")
                    .attr("x", "20%")
                    .append("g")
                    .attr("transform", "translate(0, 80)");

                var x = d3.scaleLinear()
                    .domain([this.cleanedData[0].x, this.cleanedData[this.cleanedData.length - 1].x])
                    .range([0, width]);


                var xAxis = g => g
                    .attr("transform", `translate(0, ${height + 40})`)
                    .attr("class", "x-axis");

                // Add Y axis
                var y = d3.scaleLinear()
                    .domain(adaptedYValueRange)
                    .range([ height, 0 ]);

                // Add the line
                svg.append("path")
                    .datum(this.cleanedData)
                    .attr("fill", "none")
                    .attr("stroke", "#9a6fe3")
                    .attr("stroke-width", 1.5)
                    .attr("stroke-linecap", "round")
                    .attr("stroke-dasharray", "1, 5")
                    .attr("d", d3.line().curve(d3.curveMonotoneX) // Curve the line
                        .x(function(d) {
                            return x(d.x);
                        })
                        .y(function(d) {
                            return y(d.y);
                        })
                    );

                /* Define the data for the circles */
                var point = svg.append("g")
                    .selectAll("dot")
                    .data(this.cleanedData);

                /*Create and place the "blocks" containing the circle and the text */
                var pointGroup = point.enter()
                    .append("g")
                    .attr("class", (d) => {

                        let classValue = "point-wrapper";

                        return classValue;
                    })
                    .attr("transform", function(d) {
                        return "translate("+ x(d.x) + ", " + y(d.y) + ")";
                    });

                const activePoint = pointGroup.filter((d) => {
                    if (d.highlight) {
                        return d;
                    }
                });

                /*Create the circle for each block */
                activePoint.append("circle")
                    .attr("class", "point")
                    .attr("r", (d) => {
                        let pointRadius = 35;
                        return pointRadius;
                    })
                    .attr("stroke", "#9a6fe3")
                    .attr("stroke-width", 0)
                    .attr("fill", "#fff");

                activePoint.append("text")
                    .style("text-anchor", "middle")
                    .attr("dominant-baseline", "central")
                    .attr("class", "point__text point__text--strong")
                    .text(Math.round(this.activePoint.x) + "%");

                svg.append("g")
                    .call(xAxis);

                this.chartReady = true;
            });
        },
    }
};
</script>

